import { useEffect, type RefObject } from "react";

type AnyRef = RefObject<HTMLElement | null>;

/**
 * Close a popup/menu on outside-mousedown or Escape. Pass the container ref(s)
 * that count as "inside" — e.g. a trigger wrapper plus a portaled panel.
 *
 * `capture: true` (used by the context menus) runs the outside check in the
 * capture phase so a child's `stopPropagation` can't suppress dismissal.
 */
export function useDismiss(
  onClose: () => void,
  opts: { enabled?: boolean; capture?: boolean } = {},
  ...refs: AnyRef[]
): void {
  const { enabled = true, capture = false } = opts;
  useEffect(() => {
    if (!enabled) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (refs.some((r) => r.current?.contains(t))) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("mousedown", onDown, capture);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, capture);
      window.removeEventListener("keydown", onKey);
    };
    // refs are stable useRef handles; spreading is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, enabled, capture, ...refs]);
}
